import type { PartnersEmbeddedSessionStatus, QuotingPaginatedQuoteList } from 'utils/web_api.schemas'

import { EmployerOnboardingPage } from './useOnboardingStep'

export interface EmbeddedOnboardingStepParams {
  completeEmployerProfile: boolean
  quotes?: QuotingPaginatedQuoteList
  sessionStatus?: PartnersEmbeddedSessionStatus
}

/**
 * Determines the current onboarding step for embedded onboarding flows.
 *
 * This helper encapsulates the logic for determining which step a user should be on
 * in the embedded onboarding process based on their quote status and admin status.
 *
 * @param params - The quotes and session status data
 * @returns The appropriate onboarding page for the current state
 */
export function determineEmbeddedOnboardingStep ({
  completeEmployerProfile = false,
  quotes,
  sessionStatus,
}: EmbeddedOnboardingStepParams): string {
  // If there is an existing admin for the session employer, they are already done with the embedded portion of onboarding.
  // Take them to the password setup page to show them the error message with next steps.
  if (sessionStatus?.session_employer_existing_admin_email) {
    return EmployerOnboardingPage.EMBEDDED_SET_PASSWORD
  }

  // If no processed quotes exist, user needs to complete the quote process
  if (!quotes?.data?.some(quote => quote.status === 'processed')) {
    return EmployerOnboardingPage.HOW_IT_WORKS
  }

  // If user is not yet an admin for the session employer, they haven't completed registration, so drop them off at the quote page
  if (!sessionStatus?.has_session_signed_up) {
    return EmployerOnboardingPage.QUOTE
  }

  // all info required for employer_profile_complete is set on remaining info page
  if (!completeEmployerProfile) {
    return EmployerOnboardingPage.REMAINING_INFO
  }

  // User has completed registration and can proceed to password setup/redirect
  return EmployerOnboardingPage.EMBEDDED_SET_PASSWORD
}
